import React, { useCallback, useEffect, useState } from 'react';
import {
    EuiForm,
    EuiSpacer,
    EuiFlexGroup,
    EuiFlexItem,
    EuiCard,
    EuiIcon,
    EuiTitle,
    EuiCallOut,
    EuiPanel,
    EuiFormRow,
    EuiButtonGroup,
} from '@elastic/eui';
import { useDispatch, useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import FormGroupDescription from '../../../components/formGroupDescription';
import { restaurantSettingsAPI } from '../../../api/restaurantSettings/restaurantSettingsAPI';
import { toastsErrorMessage } from '../../../utils/toasts';
import useFormActionsHandler from '../../../hooks/useFormActionsHandler';
import PointsConfiguration from '../Components/Loyalty/PointsConfiguration';
import CashbackSettings from './CashbackSettings';
import GamificationSettings from '../Components/Loyalty/GamificationSettings';
import TierSystem from '../Components/Loyalty/TierSystem';
import RewardsCatalog from '../Components/Loyalty/RewardsCatalog';

const LoyaltyProgramSettings = (props) => {
    const { restaurantResponse, fetchRestaurant } = props;
    const dispatch = useDispatch();
    const restaurantDescriptions = useSelector((state) => state.auth.restaurantDescriptions);
    const [selectedSystem, setSelectedSystem] = useState('cashback');
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateFormValues = useCallback((data) => {
        const loyaltyData = data?.settings || {};
        const isCashbackEnabled = loyaltyData.enable_reward_cashback_functionality === 1;
        const isPointsEnabled = loyaltyData.enable_points_functionality === 1;
        const isLoyaltyEnabled = isCashbackEnabled || isPointsEnabled;

        // Determine selected system based on which is enabled
        let systemType = 'cashback'; // default
        if (isPointsEnabled && !isCashbackEnabled) {
            systemType = 'points';
        } else if (isCashbackEnabled && !isPointsEnabled) {
            systemType = 'cashback';
        }
        let defaultValue = {
            // Enable loyalty program (use numeric value like CODEnablement)
            enable_loyalty_program: isLoyaltyEnabled ? 1 : 0,

            // Primary system selection
            loyalty_system_type: systemType,

            // Cashback settings
            enable_cashback: isCashbackEnabled,
            cashback_earning_percentage: loyaltyData.cashback_earning_percentage ?? 0,
            cashback_expiration_days: loyaltyData.reward_point_expiration_duration ?? 0,
            cashback_signup_bonus: loyaltyData.signup_cashback ?? 0,

            // Points settings
            enable_points: isPointsEnabled,
            points_earning_rule_amount: loyaltyData.points_earning_spend_amount ?? 0,
            points_earning_rule_points: loyaltyData.points_earned_for_spend ?? 0,
            points_expiration_days: loyaltyData.reward_point_expiration_duration ?? 0,
            points_signup_bonus: loyaltyData.signup_cashback ?? 0,
            calculate_points_after_discount: loyaltyData.calculate_points_after_discount ?? true,
            universal_earning_rule: loyaltyData.universal_earning_rule ?? true,

            // Rewards catalog
            rewards_catalog: loyaltyData.rewards_catalog || [],
            // Map API values to component values: API sends 'show_as_out_of_stock' or 'hide_from_catalog'
            // Component expects 'show_out_of_stock' or 'hide'
            out_of_stock_behavior:
                loyaltyData.out_of_stock_behavior === 'hide_from_catalog'
                    ? 'hide'
                    : loyaltyData.out_of_stock_behavior === 'show_as_out_of_stock'
                      ? 'show_out_of_stock'
                      : loyaltyData.out_of_stock_behavior || 'show_out_of_stock',

            // Gamification settings
            enable_gamification: loyaltyData.is_gamified_visit_system_enabled === 1,
            enable_daily_rewards: loyaltyData.is_daily_reward_enabled === 1,
            daily_reward_mechanism: loyaltyData.scratch_card_enabled ? 'scratch_card' : 'spin_wheel',
            daily_reward_min: loyaltyData.probability_range_start_from ?? 0,
            daily_reward_max: loyaltyData.probability_range_end_at ?? 0,
            // Determine scratch card probability mode based on reward_advance_setting_enabled
            scratch_card_probability_mode: (() => {
                // If reward_advance_setting_enabled is 0/false and reward_ranges have data, set to manual
                if (
                    loyaltyData.reward_advance_setting_enabled === 0 ||
                    loyaltyData.reward_advance_setting_enabled === false
                ) {
                    if (data.reward_ranges?.length > 0) {
                        return 'manual';
                    }
                    return 'manual';
                }

                // If reward_advance_setting_enabled is 1/true, check probability_range_type
                if (
                    loyaltyData.reward_advance_setting_enabled === 1 ||
                    loyaltyData.reward_advance_setting_enabled === true
                ) {
                    return loyaltyData.probability_range_type || 'manual';
                }

                // Default fallback
                return 'manual';
            })(),
            daily_reward_priority: loyaltyData.daily_reward_priority || 'mid', // 'low', 'mid', or 'high' - for advanced mode
            daily_reward_probability_ranges: (() => {
                // reward_ranges is at the root level of data, not in settings
                const ranges =
                    data.reward_ranges?.map((range) => ({
                        id: range.id, // Include the ID for existing ranges
                        min: range.probability_range_start_from,
                        max: range.probability_range_end_at,
                        probability: range.probability_percentage,
                    })) || [];
                return ranges;
            })(),

            enable_streaks: loyaltyData.is_streak_reward_enabled === 1 || false,
            streak_rewards:
                (loyaltyData.streak_rewards || data?.streak_rewards || loyaltyData.loyalty_streak_rewards)?.map(
                    (streak) => ({
                        id: streak.id,
                        days: streak.streak_days,
                        reward:
                            typeof streak.reward_amount === 'string'
                                ? Number.isInteger(parseFloat(streak.reward_amount))
                                    ? parseInt(streak.reward_amount)
                                    : parseFloat(streak.reward_amount)
                                : streak.reward_amount,
                    })
                ) || [],

            // Challenges
            enable_referral_challenge: loyaltyData.app_sharing_reward > 0,
            referral_challenge_reward: loyaltyData.app_sharing_reward ?? 0,

            enable_order_frequency_challenge: loyaltyData.order_frequency_count > 0,
            order_frequency_count: loyaltyData.order_frequency_count ?? 0,
            order_frequency_days: loyaltyData.order_frequency_days ?? 0,
            order_frequency_reward: loyaltyData.order_frequency_reward ?? 0,

            enable_order_diversity_challenge: loyaltyData.order_diversity_reward > 0,
            order_diversity_reward: loyaltyData.order_diversity_reward ?? 0,

            enable_profile_completion_challenge: loyaltyData.profile_completion_reward > 0,
            profile_completion_reward: loyaltyData.profile_completion_reward ?? 0,

            // Tier system
            enable_tier_system: loyaltyData.is_tier_system_enabled === 1,
            tier_count: data.tiers?.length ?? 0,
            tier_downgrade_period_days: loyaltyData.loyalty_downgrade_period ?? 0,
            tiers:
                data.tiers?.map((tier) => ({
                    id: tier.id,
                    name: tier.name,
                    ltv_threshold: parseFloat(tier.life_time_value_threshold) || 0,
                    cashback_rate: tier.earning_cashback_percentage,
                    points_earning_amount: tier.points_earning_spend_amount,
                    points_earning_points: tier.points_earned_for_spend,
                })) || [],

            // Per-mode configuration - check if all modes have same percentage
            same_redemption_all_modes: (() => {
                const configs = data?.ordering_mode_cashback_configurations || [];
                if (configs.length === 0) return true;
                const firstPercentage = configs[0]?.cashback_redeem_percentage;
                return configs.every((config) => config.cashback_redeem_percentage === firstPercentage);
            })(),
            ordering_mode_configurations:
                data?.ordering_mode_cashback_configurations?.map((config) => ({
                    restaurant_ordering_mode_id: config.restaurant_ordering_mode_id,
                    cashback_redeem_percentage: config.cashback_redeem_percentage,
                    cashback_settlement_delay: config.cashback_settlement_delay,
                })) || [],
            ordering_mode_cashback_configurations: data?.ordering_mode_cashback_configurations || [],

            // Tab ordering modes for individual configuration
            tabOrderingModes: (data?.ordering_modes || [])
                .filter((mode) => mode.status)
                .map((mode) => {
                    const config = (data?.ordering_mode_cashback_configurations || []).find(
                        (c) => c.restaurant_ordering_mode_id === mode.id
                    );
                    return {
                        ...mode,
                        restaurant_ordering_mode_id: mode.id,
                        cashback_redeem_percentage: config?.cashback_redeem_percentage ?? 0,
                        cashback_settlement_delay: config?.cashback_settlement_delay ?? 0,
                        // Preserve the delay in hours/minutes format
                        cashback_settlement_delay_in_hrs: config?.cashback_settlement_delay
                            ? Math.floor(config.cashback_settlement_delay / 60)
                            : 0,
                        cashback_settlement_delay_in_mins: config?.cashback_settlement_delay
                            ? config.cashback_settlement_delay % 60
                            : 0,
                    };
                })
                .filter((mode) => ['delivery', 'takeaway', 'dine_in'].includes(mode.tab))
                .reduce((acc, curr) => (acc.find((mode) => mode.tab === curr.tab) ? acc : [...acc, curr]), []),

            // Earnings delay
            same_delay_all_modes: loyaltyData.same_delay_all_modes ?? true,
            is_same_config_for_earnings_delay: (() => {
                const configs = data?.ordering_mode_cashback_configurations || [];
                if (configs.length === 0) return true;
                const firstDelay = configs[0]?.cashback_settlement_delay;
                return configs.every((config) => config.cashback_settlement_delay === firstDelay);
            })(),
            show_earnings_delay:
                data?.ordering_mode_cashback_configurations?.some((config) => config.cashback_settlement_delay > 0) ??
                false,

            // Convert the first ordering mode's delay from minutes to hours/mins for unified delay
            cashback_settlement_delay_in_hrs: (() => {
                const firstDelay = data?.ordering_mode_cashback_configurations?.[0]?.cashback_settlement_delay;
                return firstDelay ? Math.floor(firstDelay / 60) : 0;
            })(),
            cashback_settlement_delay_in_mins: (() => {
                const firstDelay = data?.ordering_mode_cashback_configurations?.[0]?.cashback_settlement_delay;
                return firstDelay ? firstDelay % 60 : 0;
            })(),

            // Individual ordering mode delays for EarningsDelay component
            orderingModes: (data?.ordering_modes || [])
                .filter((mode) => mode.status)
                .map((mode) => {
                    const config = (data?.ordering_mode_cashback_configurations || []).find(
                        (c) => c.restaurant_ordering_mode_id === mode.id
                    );
                    const delayMinutes = config?.cashback_settlement_delay || 0;
                    return {
                        ...mode,
                        display_name: mode.display_name || mode.type,
                        restaurant_ordering_mode_id: mode.id,
                        cashback_redeem_percentage: config?.cashback_redeem_percentage ?? 0,
                        cashback_settlement_delay: delayMinutes,
                        cashback_settlement_delay_in_hrs: delayMinutes ? Math.floor(delayMinutes / 60) : 0,
                        cashback_settlement_delay_in_mins: delayMinutes ? delayMinutes % 60 : 0,
                    };
                })
                .filter((mode) => ['delivery', 'takeaway', 'dine_in'].includes(mode.tab))
                .reduce((acc, curr) => (acc.find((mode) => mode.tab === curr.tab) ? acc : [...acc, curr]), []),

            earnings_delay_configurations: [],

            // Advanced settings
            advance_settings:
                data?.ordering_mode_cashback_configurations?.some((config) => config.cashback_redeem_percentage > 0) ??
                false,
            redeem_for_all_modes: (() => {
                const configs = data?.ordering_mode_cashback_configurations || [];
                if (configs.length === 0) return 0;
                const firstPercentage = configs[0]?.cashback_redeem_percentage;
                // Only use this value if all modes have the same percentage
                const allSame = configs.every((config) => config.cashback_redeem_percentage === firstPercentage);
                return allSame ? firstPercentage : 0;
            })(),

            // Promo consolidation
            promo_consolidation: loyaltyData.promo_consolidation === 1,
        };
        // Update selected system
        setSelectedSystem(systemType);
        return defaultValue;
    }, []);

    // Initialize with complete default values to prevent form reset issues
    const [defaultValues, setDefaultValues] = useState(() => {
        const initialData = restaurantResponse?.restaurant || {};
        return updateFormValues(initialData);
    });

    const onFormSaveApi = useCallback(
        async (data) => {
            setIsSubmitting(true);

            try {
                // Determine which system is enabled (handle both numeric and boolean values)
                const loyaltyEnabled = data.enable_loyalty_program === 1 || data.enable_loyalty_program === true;
                const isCashbackEnabled = selectedSystem === 'cashback' && loyaltyEnabled;
                const isPointsEnabled = selectedSystem === 'points' && loyaltyEnabled;
                const isTierSystemEnabled = data.enable_tier_system;

                // Base API payload
                const apiPayload = {
                    section: 'loyalty_settings',
                    enable_reward_cashback_functionality: isCashbackEnabled ? 1 : 0,
                    enable_points_functionality: isPointsEnabled ? 1 : 0,
                    is_tier_system_enabled: isTierSystemEnabled ? 1 : 0,
                };
                // Add tier system related fields
                if (isTierSystemEnabled) {
                    apiPayload.loyalty_downgrade_period = Number(data.tier_downgrade_period_days) || 365;
                    apiPayload.tiers = data.tiers?.map((tier, index) => {
                        const tierData = {
                            name: tier.name,
                            life_time_value_threshold: Number(tier.ltv_threshold),
                        };

                        // Add id only for existing tiers with numeric IDs
                        // Ignore UUIDs or other non-numeric IDs that come from react-hook-form
                        if (tier.id && typeof tier.id === 'number') {
                            tierData.id = tier.id;
                        }

                        // Add ONLY the fields for the selected system
                        if (isCashbackEnabled) {
                            // Only add cashback field for cashback system
                            tierData.earning_cashback_percentage = Number(tier.cashback_rate);
                        } else if (isPointsEnabled) {
                            // Only add points fields for points system
                            tierData.points_earned_for_spend = Number(tier.points_earning_points);
                            tierData.points_earning_spend_amount = Number(tier.points_earning_amount);
                        }

                        return tierData;
                    });
                } else {
                    if (isCashbackEnabled || isPointsEnabled) {
                        apiPayload.cashback_earning_percentage = Number(data.cashback_earning_percentage);
                    }

                    if (isPointsEnabled) {
                        apiPayload.points_earned_for_spend = Number(data.points_earning_rule_points);
                        apiPayload.points_earning_spend_amount = Number(data.points_earning_rule_amount);
                    }
                }

                // Common fields when either system is enabled
                if (isCashbackEnabled || isPointsEnabled) {
                    apiPayload.reward_point_expiration_duration = Number(
                        isCashbackEnabled ? data.cashback_expiration_days : data.points_expiration_days
                    );
                    apiPayload.signup_cashback = Number(
                        isCashbackEnabled ? data.cashback_signup_bonus : data.points_signup_bonus
                    );
                    apiPayload.promo_consolidation = data.promo_consolidation || false;

                    // Ordering mode configurations
                    // Build ordering_mode_cashback_configurations based on available data
                    const orderingModeConfigs = [];

                    // Check if we have advance settings data (tabOrderingModes)
                    const hasAdvanceSettings =
                        data.tabOrderingModes?.length > 0 &&
                        (data.advance_settings ||
                            data.same_redemption_all_modes ||
                            data.redeem_for_all_modes ||
                            data.tabOrderingModes.some((mode) => mode.cashback_redeem_percentage));

                    // Check if we have earnings delay data
                    // When is_same_config_for_earnings_delay is true, we use the unified delay values
                    const hasEarningsDelay =
                        (data.show_earnings_delay || data.is_same_config_for_earnings_delay) &&
                        (data.cashback_settlement_delay_in_hrs ||
                            data.cashback_settlement_delay_in_mins ||
                            (data.orderingModes?.length > 0 &&
                                data.orderingModes.some(
                                    (mode) =>
                                        mode.cashback_settlement_delay_in_hrs || mode.cashback_settlement_delay_in_mins
                                )));

                    if (hasAdvanceSettings || hasEarningsDelay) {
                        // Build configuration based on tabOrderingModes as the primary source
                        // since it has the complete mode information including IDs
                        if (data.tabOrderingModes?.length > 0) {
                            apiPayload.ordering_mode_cashback_configurations = data.tabOrderingModes.map(
                                (mode, index) => {
                                    const config = {
                                        restaurant_ordering_mode_id: Number(mode.restaurant_ordering_mode_id),
                                    };

                                    // Add cashback redeem percentage (required when cashback OR points enabled)
                                    if (isCashbackEnabled || isPointsEnabled) {
                                        config.cashback_redeem_percentage = data.same_redemption_all_modes
                                            ? Number(data.redeem_for_all_modes || 0)
                                            : Number(mode.cashback_redeem_percentage || 0);
                                    }

                                    // Add delay if available
                                    let delayInMinutes = 0;

                                    if (data.is_same_config_for_earnings_delay) {
                                        // Use unified delay values for all modes
                                        delayInMinutes =
                                            (Number(data.cashback_settlement_delay_in_hrs) || 0) * 60 +
                                            (Number(data.cashback_settlement_delay_in_mins) || 0);
                                    } else if (data.orderingModes?.[index]) {
                                        // Use individual mode delay values
                                        const delayData = data.orderingModes[index];
                                        delayInMinutes =
                                            (Number(delayData.cashback_settlement_delay_in_hrs) || 0) * 60 +
                                            (Number(delayData.cashback_settlement_delay_in_mins) || 0);
                                    }

                                    if (delayInMinutes > 0) {
                                        config.cashback_settlement_delay = delayInMinutes;
                                    }

                                    return config;
                                }
                            );
                        } else if (hasEarningsDelay && restaurantResponse?.restaurant?.ordering_modes) {
                            // If we only have delay data, build config from restaurant ordering modes
                            const activeOrderingModes = restaurantResponse.restaurant.ordering_modes.filter(
                                (mode) => mode.status
                            );
                            const configuredModes =
                                restaurantResponse.restaurant.ordering_mode_cashback_configurations || [];

                            apiPayload.ordering_mode_cashback_configurations = activeOrderingModes
                                .filter((mode, index) => index < data.orderingModes.length)
                                .map((mode, index) => {
                                    const existingConfig = configuredModes.find(
                                        (c) => c.restaurant_ordering_mode_id === mode.id
                                    );
                                    const config = {
                                        restaurant_ordering_mode_id: Number(mode.id),
                                    };

                                    // Use existing cashback percentage or default (required when cashback OR points enabled)
                                    if (isCashbackEnabled || isPointsEnabled) {
                                        config.cashback_redeem_percentage =
                                            existingConfig?.cashback_redeem_percentage ||
                                            Number(data.redeem_for_all_modes || 0);
                                    }

                                    // Add delay from form data
                                    let delayInMinutes = 0;
                                    if (data.is_same_config_for_earnings_delay) {
                                        delayInMinutes =
                                            (Number(data.cashback_settlement_delay_in_hrs) || 0) * 60 +
                                            (Number(data.cashback_settlement_delay_in_mins) || 0);
                                    } else {
                                        const delayData = data.orderingModes[index];
                                        delayInMinutes =
                                            (Number(delayData.cashback_settlement_delay_in_hrs) || 0) * 60 +
                                            (Number(delayData.cashback_settlement_delay_in_mins) || 0);
                                    }

                                    if (delayInMinutes > 0) {
                                        config.cashback_settlement_delay = delayInMinutes;
                                    }

                                    return config;
                                });
                        }
                    } else if (data.ordering_mode_configurations?.length > 0) {
                        // Use individual configurations per mode (legacy path)
                        apiPayload.ordering_mode_cashback_configurations = data.ordering_mode_configurations.map(
                            (config) => {
                                const modeConfig = {
                                    restaurant_ordering_mode_id: Number(config.restaurant_ordering_mode_id),
                                };

                                if (isCashbackEnabled || isPointsEnabled) {
                                    modeConfig.cashback_redeem_percentage = Number(config.cashback_redeem_percentage);
                                }

                                if (config.cashback_settlement_delay) {
                                    modeConfig.cashback_settlement_delay = Number(config.cashback_settlement_delay);
                                }

                                return modeConfig;
                            }
                        );
                    } else {
                        // Default empty array
                        apiPayload.ordering_mode_cashback_configurations = [];
                    }
                }

                // Gamification settings
                apiPayload.is_gamified_visit_system_enabled = data.enable_gamification ? 1 : 0;

                if (data.enable_gamification == 1 || data.enable_gamification === true) {
                    // Only send challenge values if the specific challenge is enabled
                    apiPayload.app_sharing_reward = data.enable_referral_challenge
                        ? Number(data.referral_challenge_reward) || 0
                        : 0;
                    apiPayload.order_frequency_count = data.enable_order_frequency_challenge
                        ? Number(data.order_frequency_count) || 0
                        : 0;
                    apiPayload.order_frequency_days = data.enable_order_frequency_challenge
                        ? Number(data.order_frequency_days) || 0
                        : 0;
                    apiPayload.order_frequency_reward = data.enable_order_frequency_challenge
                        ? Number(data.order_frequency_reward) || 0
                        : 0;
                    apiPayload.order_diversity_reward = data.enable_order_diversity_challenge
                        ? Number(data.order_diversity_reward) || 0
                        : 0;
                    apiPayload.profile_completion_reward = data.enable_profile_completion_challenge
                        ? Number(data.profile_completion_reward) || 0
                        : 0;
                    apiPayload.is_streak_reward_enabled = data.enable_streaks ? 1 : 0;
                    apiPayload.is_daily_reward_enabled = data.enable_daily_rewards ? 1 : 0;

                    // Handle daily rewards configuration
                    if (data.enable_daily_rewards) {
                        // Set reward mechanism
                        if (data.daily_reward_mechanism === 'scratch_card') {
                            apiPayload.scratch_card_enabled = true;
                            apiPayload.spin_wheel_enabled = false;
                        } else if (data.daily_reward_mechanism === 'spin_wheel') {
                            apiPayload.spin_wheel_enabled = true;
                            apiPayload.scratch_card_enabled = false;
                        }

                        // Handle probability mode
                        if (data.scratch_card_probability_mode === 'manual') {
                            // Manual mode: pass reward_ranges, don't pass probability_range_type
                            // reward_advance_setting_enabled should be false
                            apiPayload.reward_advance_setting_enabled = false;

                            // Pass reward ranges for manual mode with correct field names
                            if (data.daily_reward_probability_ranges?.length > 0) {
                                apiPayload.reward_ranges = data.daily_reward_probability_ranges.map((range) => {
                                    const rangeData = {
                                        probability_range_start_from: Number(range.min),
                                        probability_range_end_at: Number(range.max),
                                        probability_percentage: Number(range.probability),
                                    };
                                    // Include ID for existing ranges (numeric IDs only)
                                    if (range.id && typeof range.id === 'number') {
                                        rangeData.id = range.id;
                                    }
                                    return rangeData;
                                });
                            }
                        } else if (
                            ['conservative', 'moderate', 'aggressive'].includes(data.scratch_card_probability_mode)
                        ) {
                            // Conservative/Moderate/Aggressive mode
                            // Pass probability_range_type and set reward_advance_setting_enabled to true
                            apiPayload.probability_range_type = data.scratch_card_probability_mode;
                            apiPayload.reward_advance_setting_enabled = 1;

                            // Pass the min and max range values
                            apiPayload.probability_range_start_from = Number(data.daily_reward_min) || 0;
                            apiPayload.probability_range_end_at = Number(data.daily_reward_max) || 0;
                        }
                    }
                    if (data.enable_streaks && data.streak_rewards?.length > 0) {
                        apiPayload.streak_rewards = data.streak_rewards.map((streak, index) => {
                            const streakData = {
                                streak_days: Number(streak.days),
                                reward_amount: Number(streak.reward),
                            };
                            // Only include id if it exists (for existing streaks)
                            if (streak.id) {
                                streakData.id = streak.id;
                            }
                            return streakData;
                        });
                    }
                }

                // Handle out of stock behavior for rewards catalog (points system)
                if (isPointsEnabled) {
                    // Map the out_of_stock_behavior value to the API expected format
                    if (data.out_of_stock_behavior === 'show_out_of_stock') {
                        apiPayload.out_of_stock_behavior = 'show_as_out_of_stock';
                    } else if (data.out_of_stock_behavior === 'hide') {
                        apiPayload.out_of_stock_behavior = 'hide_from_catalog';
                    }

                    // Pass rewards catalog if it exists
                    if (data.rewards_catalog?.length > 0) {
                        apiPayload.items = data.rewards_catalog;
                    }
                }
                const response = await dispatch(restaurantSettingsAPI('loyalty_settings', apiPayload));

                if (response.success) {
                    // Wait for fetchRestaurant to complete before clearing submitting flag
                    await fetchRestaurant();

                    // Set flag after fetch completes to allow form update
                    setIsSubmitting(false);
                } else {
                    toastsErrorMessage(response?.message, dispatch);
                    setIsSubmitting(false);
                }

                return response;
            } catch (error) {
                toastsErrorMessage('An unexpected error occurred.', dispatch);
                setIsSubmitting(false);
                return { success: false, message: error.message };
            }
        },
        [dispatch, fetchRestaurant, selectedSystem, restaurantResponse]
    );

    const { errors, control, watch, setValue, getValues, reset, trigger } = useFormActionsHandler({
        onFormSaveApi,
        defaultValues,
    });

    useEffect(() => {
        if (restaurantResponse?.restaurant && !isSubmitting) {
            const formData = updateFormValues(restaurantResponse.restaurant);
            setDefaultValues(formData);

            // Reset form with new default values to clear dirty state
            reset(formData);

            if (!isInitialized) {
                setIsInitialized(true);
                // Ensure the correct system is enabled based on selection
                if (formData.loyalty_system_type === 'cashback' && !formData.enable_cashback) {
                    setValue('enable_cashback', true, { shouldDirty: true });
                } else if (formData.loyalty_system_type === 'points' && !formData.enable_points) {
                    setValue('enable_points', true, { shouldDirty: true });
                }
            }
        }
    }, [restaurantResponse?.restaurant?.id, isSubmitting, reset, updateFormValues, setValue]); // Run when restaurant ID changes or submission state changes

    // Remove this useEffect as it might be causing unnecessary updates
    // The initial values are already set in defaultValues

    const handleSystemChange = useCallback(
        (system) => {
            setSelectedSystem(system);
            setValue('loyalty_system_type', system, { shouldDirty: true });

            // Enable the selected system and disable the other
            if (system === 'cashback') {
                setValue('enable_cashback', true, { shouldDirty: true });
                setValue('enable_points', false, { shouldDirty: true });
            } else {
                setValue('enable_points', true, { shouldDirty: true });
                setValue('enable_cashback', false, { shouldDirty: true });
            }
        },
        [setValue]
    );
    return (
        <EuiForm component='form'>
            <FormGroupDescription
                title='Loyalty Program Settings'
                description='Configure your loyalty program to reward customers and increase engagement'
            >
                {/* Enable Loyalty Program Toggle */}
                <EuiFormRow label='Loyalty Module'>
                    <Controller
                        render={({ field }) => (
                            <EuiButtonGroup
                                options={[
                                    {
                                        id: 1,
                                        label: 'Enable',
                                        iconType: 'check',
                                    },
                                    {
                                        id: 0,
                                        label: 'Disable',
                                        iconType: 'cross',
                                    },
                                ]}
                                idSelected={field.value}
                                onChange={field.onChange}
                                color='primary'
                            />
                        )}
                        name='enable_loyalty_program'
                        control={control}
                    />
                </EuiFormRow>

                <EuiSpacer size='xl' />

                {watch('enable_loyalty_program') === 1 || watch('enable_loyalty_program') === true ? (
                    <EuiPanel paddingSize='l' style={{ minWidth: '500px' }}>
                        {/* Primary System Selection */}
                        <EuiTitle size='s'>
                            <h3>Select Your Primary Loyalty System</h3>
                        </EuiTitle>
                        <EuiSpacer size='m' />

                        <EuiFlexGroup gutterSize='l'>
                            <EuiFlexItem>
                                <EuiCard
                                    title='Cashback System'
                                    description='Traditional percentage-based rewards'
                                    icon={<EuiIcon size='xl' type='currency' />}
                                    selectable={{
                                        onClick: () => handleSystemChange('cashback'),
                                        isSelected: selectedSystem === 'cashback',
                                    }}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem>
                                <EuiCard
                                    title='Points System'
                                    description='Earn points & redeem for menu items'
                                    icon={<EuiIcon size='xl' type='starFilled' />}
                                    selectable={{
                                        onClick: () => handleSystemChange('points'),
                                        isSelected: selectedSystem === 'points',
                                    }}
                                />
                            </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size='l' />

                        <EuiCallOut title='Note' color='warning' iconType='alert' size='s'>
                            <p>You can only run one system at a time. Switching systems will disable the other.</p>
                        </EuiCallOut>

                        <EuiSpacer size='xl' />

                        {/* System-specific Configuration */}
                        {selectedSystem === 'cashback' ? (
                            <>
                                {/* Tier System */}
                                <TierSystem
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    selectedSystem={selectedSystem}
                                    trigger={trigger}
                                />
                                <EuiSpacer size='xl' />

                                {/* Cashback Settings (Earning %, Expiry, Signup Bonus, Advanced) */}
                                <CashbackSettings
                                    restaurantResponse={restaurantResponse}
                                    enableTierSystem={watch('enable_tier_system')}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    getValues={getValues}
                                    fetchRestaurant={fetchRestaurant}
                                    selectedSystem={selectedSystem}
                                />

                                <EuiSpacer size='xl' />

                                {/* Gamification & Challenges */}
                                <GamificationSettings
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    selectedSystem={selectedSystem}
                                />
                            </>
                        ) : (
                            <>
                                {/* Tier System */}
                                <TierSystem
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    selectedSystem={selectedSystem}
                                    trigger={trigger}
                                />
                                <EuiSpacer size='xl' />

                                {/* Points Configuration (Expiry, Signup Bonus) */}
                                <PointsConfiguration
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    enableTierSystem={watch('enable_tier_system')}
                                />

                                <EuiSpacer size='xl' />

                                {/* Advanced Settings (for Points system) */}
                                <CashbackSettings
                                    restaurantResponse={restaurantResponse}
                                    enableTierSystem={watch('enable_tier_system')}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    getValues={getValues}
                                    fetchRestaurant={fetchRestaurant}
                                />

                                <EuiSpacer size='xl' />

                                {/* Gamification & Challenges */}
                                <GamificationSettings
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    selectedSystem={selectedSystem}
                                />

                                <EuiSpacer size='xl' />

                                {/* Rewards Catalog */}
                                <RewardsCatalog
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setValue={setValue}
                                    getValues={getValues}
                                />
                            </>
                        )}
                    </EuiPanel>
                ) : null}
            </FormGroupDescription>
        </EuiForm>
    );
};

export default LoyaltyProgramSettings;
